import { User01 } from "@untitledui/icons";
import { type FC, type ReactNode, useState } from "react";

import { cx } from "@/utils/cx";

import { AvatarOnlineIndicator, VerifiedTick } from "./base-components";

export interface AvatarProps {
	alt?: string;
	/**
	 * Display a badge (i.e. company logo).
	 */
	badge?: ReactNode;
	className?: string;
	/**
	 * Display a contrast border around the avatar.
	 */
	contrastBorder?: boolean;
	/**
	 * Whether the avatar should show a focus ring when the parent group is in focus.
	 * For example, when the avatar is wrapped inside a link.
	 *
	 * @default false
	 */
	focusable?: boolean;
	/**
	 * The initials of the user to display if no image is available.
	 */
	initials?: string;
	/**
	 * A placeholder to display if no image is available.
	 */
	placeholder?: ReactNode;
	/**
	 * An icon to display if no image is available.
	 */
	placeholderIcon?: FC<{ className?: string }>;

	size?: AvatarSize;
	src?: null | string;
	/**
	 * Display a status indicator.
	 */
	status?: "offline" | "online";

	/**
	 * Display a verified tick icon.
	 *
	 * @default false
	 */
	verified?: boolean;
}

type AvatarSize = "2xl" | "lg" | "md" | "sm" | "xl" | "xs" | "xxs";

const styles = {
	"2xl": {
		icon: "size-8",
		initials: "text-display-xs font-semibold",
		root: "size-16 outline-1 -outline-offset-1",
	},
	lg: {
		icon: "size-7",
		initials: "text-lg font-semibold",
		root: "size-12 outline-1 -outline-offset-1",
	},
	md: {
		icon: "size-6",
		initials: "text-md font-semibold",
		root: "size-10 outline-1 -outline-offset-1",
	},
	sm: {
		icon: "size-5",
		initials: "text-sm font-semibold",
		root: "size-8 outline-[0.75px] -outline-offset-[0.75px]",
	},
	xl: {
		icon: "size-8",
		initials: "text-xl font-semibold",
		root: "size-14 outline-1 -outline-offset-1",
	},
	xs: {
		icon: "size-4",
		initials: "text-xs font-semibold",
		root: "size-6 outline-[0.5px] -outline-offset-[0.5px]",
	},
	xxs: {
		icon: "size-3",
		initials: "text-xs font-semibold",
		root: "size-4 outline-[0.5px] -outline-offset-[0.5px]",
	},
};

export const Avatar = ({
	alt,
	badge,
	className,
	contrastBorder = true,
	focusable = false,
	initials,
	placeholder,
	placeholderIcon: PlaceholderIcon,
	size = "md",
	src,
	status,
	verified,
}: AvatarProps) => {
	const [isFailed, setIsFailed] = useState(false);

	const renderMainContent = () => {
		if (src && !isFailed) {
			return (
				<img
					alt={alt}
					className="size-full rounded-full object-cover"
					data-avatar-img
					onError={() => setIsFailed(true)}
					src={src}
				/>
			);
		}

		if (initials) {
			return (
				<span className={cx("text-quaternary", styles[size].initials)}>
					{initials}
				</span>
			);
		}

		if (PlaceholderIcon) {
			return (
				<PlaceholderIcon
					className={cx("text-fg-quaternary", styles[size].icon)}
				/>
			);
		}

		return (
			placeholder || (
				<User01 className={cx("text-fg-quaternary", styles[size].icon)} />
			)
		);
	};

	const renderBadgeContent = () => {
		if (status) {
			return (
				<AvatarOnlineIndicator
					size={size === "xxs" ? "xs" : size}
					status={status}
				/>
			);
		}

		if (verified) {
			return (
				<VerifiedTick
					className={cx(
						"absolute right-0 bottom-0",
						(size === "xxs" || size === "xs") && "-right-px -bottom-px",
					)}
					size={size === "xxs" ? "xs" : size}
				/>
			);
		}

		return badge;
	};

	return (
		<div
			className={cx(
				"relative inline-flex shrink-0 items-center justify-center rounded-full bg-tertiary outline-transparent",
				// Focus styles
				focusable &&
					"group-outline-focus-ring group-focus-visible:outline-2 group-focus-visible:outline-offset-2",
				contrastBorder && "outline outline-avatar-contrast-border",
				styles[size].root,
				className,
			)}
			data-avatar
		>
			{renderMainContent()}
			{renderBadgeContent()}
		</div>
	);
};
